import type { ReactNode } from 'react';
import { App } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { archivedTierOf } from '@pams/shared';
import type { ArrangementDto, ArrangementStatus, DeleteImpactDto } from '@pams/shared';
import { api } from '@/lib/api';
import type { ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { can, mayArchiveTier, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { ARCHIVE_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §8] ONE ARCHIVE FLOW, TWO CALLERS — AND THE SECOND CALLER IS WHY IT EXISTS.
 *
 *  §8 puts Discard/Archive/Restore on the arrangements LIST row. The tier machinery it needs
 *  is ~80 lines of branching over ten strings that `pages/ArrangementDetail` already owned, so
 *  this is that flow EXTRACTED — CLAUDE.md §13's rule for exactly this shape: *"For a SECOND
 *  SCREEN THAT WRITES THE SAME RECORD, copy `useFillSheet` — and do not copy the mutations."*
 *  Transcribing it would be two vocabularies for one act, which is what [C1e §6b] and
 *  `ConfirmDestructive`'s header both exist to prevent.
 *
 *  ⚠️ `modal.confirm` FROM `App.useApp()`, NEVER A `<Modal>` OF ITS OWN. It is imperative, so it
 *  contributes no `<Modal` token to either caller's file — `pages/Arrangements/index.tsx` is
 *  EXEMPT at exactly two in `e2e/fixtures/overlays.ts` and `census.spec.ts`'s per-file arithmetic
 *  is exact. That is the whole reason for the primitive, not a style preference.
 *
 *  ⚠️ THE TIER IS FETCHED PER CLICK, AND ON A LIST THAT IS THE ONLY AFFORDABLE SHAPE. Three
 *  reasons, in order: `GET /:id/delete-impact` is four clock comparisons plus a cascade read PER
 *  ARRANGEMENT, so a twenty-five-row page would issue twenty-five on every render and every sort;
 *  the tier flips on the CLOCK WITH NO WRITE ([C1e §5]), so an eagerly-fetched tier is stale by
 *  the time it is clicked and a stale `FILING` shown as `CANCELLATION` is the wrong dialog on a
 *  destructive act; and `mayArchiveTier`'s own header says the courtesy refusal is possible *only*
 *  because the impact is already awaited.
 *
 *  ⚠️ AND `onDone` IS THE CALLER'S, BECAUSE THE TWO INVALIDATE DIFFERENT THINGS. The detail page
 *  refreshes the record it is showing; the list refreshes its page. A shared invalidation would
 *  have to name `queryKeys.arrangements.all`, which is a PREFIX of `detail(id)` — [P3b.3]'s exact
 *  defect, and the thing `saveKeyIsolation.test.ts` exists to refuse.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type ArchivableArrangement = {
  id: string;
  status: ArrangementStatus;
  archivedAt: string | null;
  archivedFromStatus: ArrangementStatus | null;
};

export type UseArchiveArrangement = {
  /** The three-tier flow: fetch the impact, refuse or confirm, then write. */
  confirmArchive: (row: ArchivableArrangement) => Promise<void>;
  restore: (row: ArchivableArrangement) => void;
  /** Whether this caller may restore THIS row — the archive run backwards, same gate. */
  mayRestore: (row: ArchivableArrangement) => boolean;
  isArchiving: boolean;
  isRestoring: boolean;
};

export const useArchiveArrangement = ({ onDone }: { onDone: () => void }): UseArchiveArrangement => {
  const { message, modal } = App.useApp();
  const { officer } = useAuth();
  const canDelete = officer ? can(officer.accessRole, 'arrangement.delete') : false;

  const archive = useMutation({
    mutationFn: (id: string) => api.post<ArrangementDto>(`/arrangements/${id}/archive`),
    onSuccess: () => {
      void message.success('Moved to the Archive');
      onDone();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.post<ArrangementDto>(`/arrangements/${id}/restore`),
    onSuccess: () => {
      void message.success('Restored');
      onDone();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ [C2b §4g] A CASCADE IS NAMED IN ALL THREE DIALOGS OR IN NONE. Archiving a corrigendum
   * parent takes its whole family, and a confirmation that mentions it in only one of the three
   * teaches the operator that the other two do not cascade.
   *
   * ⚠️ IT NAMES THE ORDERS AND THEIR OWN COUNTS. `impact.cascade` deliberately keeps them
   * separate from the totals beside it, so this must not sum them — a single figure would be a
   * third number that matches neither.
   */
  const cascadeLine = (cascade: DeleteImpactDto['cascade']): ReactNode =>
    cascade.length === 0 ? null : (
      <span data-testid="cascade-note">
        {ARCHIVE_COPY.cascade(cascade.map((row) => row.paNumber ?? row.reference))}
      </span>
    );

  const confirmArchive = async (row: ArchivableArrangement): Promise<void> => {
    const impact = await api.get<DeleteImpactDto>(`/arrangements/${row.id}/delete-impact`);

    if (impact.tier === 'BLOCKED') {
      // The server's own two sentences, chosen the same way it chooses them.
      void message.error(impact.hasOpenedWindow ? ARCHIVE_COPY.archiveBlocked : ARCHIVE_COPY.archiveBlockedEvent);
      return;
    }

    /*
     * ⚠️ [C1e §5] AFTER BLOCKED, MIRRORING THE SERVER — a STATE refusal outranks a PERMISSION
     * one. A blocked order may be archived by nobody, so answering 403 there would name the
     * person when the truth is the record; `archiveArrangement` orders its two guards exactly
     * this way and `arrangementArchiveTiers.test.ts` CLAIM 9 pins it.
     *
     * ⚠️ `status !== 'DRAFT'`, NEVER `!impact.isIssued`. The server's short-circuit is
     * `origin === 'DRAFT'`. `ARRANGEMENT_STATUSES_ISSUED` is its exact complement TODAY, and
     * `arrangement.schema.ts` refuses to derive one from the other because that is true by
     * arithmetic rather than by intent — so this mirrors the server literally.
     */
    if (row.status !== 'DRAFT' && officer) {
      if (!mayArchiveTier(officer.accessRole, actingOrgUnitTypeOf(officer), impact.tier)) {
        void message.error(impact.tier === 'FILING' ? ARCHIVE_COPY.archiveNotYours : ARCHIVE_COPY.archiveNotYoursLive);
        return;
      }
    }

    /*
     * ⚠️ FILING NAMES NO OFFICERS AND CARRIES NO `danger`. The duty is over — there is
     * nothing dangerous about putting the record away, and an officer count here would read
     * as a list of people about to be messaged.
     */
    if (impact.tier === 'FILING') {
      modal.confirm({
        title: ARCHIVE_COPY.archiveFileTitle,
        content: (
          <div className="flex flex-col gap-1 text-sm">
            <span>{ARCHIVE_COPY.archiveFileBody}</span>
            <span>{ARCHIVE_COPY.archiveFileDuties(impact.deploymentCount)}</span>
            {cascadeLine(impact.cascade)}
            <span className="text-muted-foreground">{ARCHIVE_COPY.archiveFileSilent}</span>
          </div>
        ),
        okText: ARCHIVE_COPY.archiveFileOk,
        onOk: () => {
          archive.mutate(row.id);
        },
      });
      return;
    }

    modal.confirm({
      /* ⚠️ [C1f §10] THE ACT, AND IT DIFFERS BY RECORD. A draft was never issued, so there is
         nothing to withdraw from it — `isIssued` is already on the impact this awaited. */
      title: impact.isIssued ? ARCHIVE_COPY.archiveTitle : ARCHIVE_COPY.archiveDraftTitle,
      content: (
        <div className="flex flex-col gap-1 text-sm">
          <span>{ARCHIVE_COPY.archiveDuties(impact.deploymentCount)}</span>
          {cascadeLine(impact.cascade)}
          {impact.affectedOfficerCount > 0 && (
            <span>
              {impact.affectedOfficerCount} {impact.affectedOfficerCount === 1 ? 'officer is' : 'officers are'} already
              named on it.
            </span>
          )}
          <span className="text-muted-foreground">
            {impact.isIssued ? ARCHIVE_COPY.archiveRestorableIssued : ARCHIVE_COPY.archiveRestorableDraft}
          </span>
        </div>
      ),
      okText: impact.isIssued ? ARCHIVE_COPY.archiveOk : ARCHIVE_COPY.archiveDraftOk,
      okButtonProps: { danger: true },
      onOk: () => {
        archive.mutate(row.id);
      },
    });
  };

  /*
   * ⚠️ [C1e §5] A RESTORE IS THE ARCHIVE RUN BACKWARDS, SO IT TAKES THE SAME GATE — and it
   * reads the RECORD rather than fetching an impact, because `archivedTierOf` DECODES the
   * stored status. `archiveArrangement` is the only writer of a `CANCELLED` arrangement, so
   * `status === 'CANCELLED'` IS "this archive was a cancellation", in both directions.
   *
   * ⚠️ IT DECODES `status`, NEVER `archivedFromStatus`: that reads a cancelled order as
   * `PUBLISHED → FILING` and would hand every CP-office nodal the power to un-cancel a
   * withdrawn order.
   */
  const mayRestore = (row: ArchivableArrangement): boolean => {
    if (!canDelete || !officer || row.archivedAt === null) return false;
    if (row.archivedFromStatus === 'DRAFT') return true;
    return mayArchiveTier(officer.accessRole, actingOrgUnitTypeOf(officer), archivedTierOf(row.status));
  };

  return {
    confirmArchive,
    restore: (row) => {
      restoreMutation.mutate(row.id);
    },
    mayRestore,
    isArchiving: archive.isPending,
    isRestoring: restoreMutation.isPending,
  };
};
